import {
  FacebookSettingKey,
  SettingKey,
} from '$extensionSrc/recommendations/recommendationSettingsKeys'

import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  allPrivacySettingsFriendsOrStricter,
  getUniquePrivacyLevelCounts,
} from './facebookRecommendationCardDisplayUtils'
import {
  clickOnPhotoById,
  gatherEditPrivacyIconValue,
  getPhotoIdFromCurrentUrl,
  getVisiblePhotoUrls,
  goToAlbum,
  hasAlbum,
  scrollToBottom,
  setEditPrivacyIconValueAsStrictOrStricter,
  setEditPrivacyIconValueExact,
  waitForAlbumsToLoad,
} from './fbAlbumPhotoViewUtils'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import { albumsUrl } from './fbUrls'
import { FACEBOOK_PHOTO_STAGE_TIMEOUT } from '../../consts/stageMachineConsts'
import { FBRecommendationKey, StageType } from '../../utils/enums'
import { _debug } from '../../utils/logging'
import { StageDef } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getInitialValueBySettingKey } from '../utils'

// Setting property functions
export const OLD_INITIAL_VALUE_KEY = 'coverPhotoSettings'
export const INITIAL_VALUE_KEY: SettingKey =
  FacebookSettingKey.COVER_PHOTO_PRIVACY

function getCurrentValueCover(initialState): string | null {
  if (initialState === null || initialState === undefined) {
    return null
  }

  if (initialState[INITIAL_VALUE_KEY] === null) {
    return 'No old cover photos'
  }

  if (initialState[INITIAL_VALUE_KEY]) {
    return getUniquePrivacyLevelCounts(initialState[INITIAL_VALUE_KEY])
  }

  return null
}

function getIsFulfilledCover(initialState): boolean {
  if (initialState === null || initialState === undefined) {
    return false
  }

  if (initialState[INITIAL_VALUE_KEY] === null) {
    return true
  }

  if (initialState[INITIAL_VALUE_KEY]) {
    return allPrivacySettingsFriendsOrStricter(initialState[INITIAL_VALUE_KEY])
  }

  return false
}

const getCurrentValueFn = () => getCurrentValueCover
const getIsFulfilledFn = () => getIsFulfilledCover
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Automations
enum StageKey {
  GATHER_SETTINGS_COVER_PHOTO_PRIVACY = 'GATHER_SETTINGS_COVER_PHOTO_PRIVACY',
  UPDATE_SETTINGS_COVER_PHOTO_PRIVACY = 'UPDATE_SETTINGS_COVER_PHOTO_PRIVACY',
  UNDO_SETTINGS_COVER_PHOTO_PRIVACY = 'UNDO_SETTINGS_COVER_PHOTO_PRIVACY',
}

async function checkForUneditableCoverPhoto() {
  const sharedWithTextPromise =
    promises.getWaitForElementToAppearPromise<HTMLElement>(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          'descendant::*[contains(text(), "Shared with")]',
        )[0],
    )

  const editPrivacyIconPromise =
    promises.getWaitForElementToAppearPromise<HTMLElement>(document, (node) => {
      const icons = selectAudienceUtils.getEditPrivacyIcons(node)
      return icons && icons.length ? icons[0] : null
    })

  // Wrap with timeouts
  const wrappedSharedWithTextPromise = Promise.any([
    sharedWithTextPromise.then(() => true),
    promises.wait(5000).then(() => false),
  ])
  const wrappedEditPrivacyIconPromise = Promise.any([
    editPrivacyIconPromise.then(() => true),
    promises.wait(5000).then(() => false),
  ])

  const [foundSharedWithText, foundEditPrivacyIcon] = await Promise.all([
    wrappedSharedWithTextPromise,
    wrappedEditPrivacyIconPromise,
  ])

  if (foundSharedWithText && !foundEditPrivacyIcon) {
    _debug(
      `Found shared with text but not edit audience icon; uneditable ${window.location.href}`,
      'automation',
    )
    return true
  } else if (foundSharedWithText && foundEditPrivacyIcon) {
    return false
  } else if (!foundSharedWithText && foundEditPrivacyIcon) {
    _debug(
      'Found edit audience icon but not shared with text',
      'automation',
      'e',
    )
    return false
  }
  throw Error('Could not find edit audience icon or shared with text')
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTINGS_COVER_PHOTO_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    action: async (machineContext, userContext, additionalMachineData) => {
      const { initialValues: storedInitialValues, updateStoredInitialValues } =
        userContext
      const { resumeData } = additionalMachineData
      const processedPhotoIds = new Set(resumeData.data.processedPhotoIds || [])

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        INITIAL_VALUE_KEY,
        OLD_INITIAL_VALUE_KEY,
      )

      await waitForAlbumsToLoad({ timeoutMs: 5000 })

      // Check first (and exit early) if no cover photos
      if (!hasAlbum('Cover photos')) {
        updateStoredInitialValues(
          { [INITIAL_VALUE_KEY]: null },
          FBRecommendationKey.FB_COVER_PHOTO_PRIVACY,
        )
        return undefined
      }

      goToAlbum('Cover photos')
      await scrollToBottom()

      const photoUrls = getVisiblePhotoUrls()
      const photoIds = photoUrls
        .map((url) => {
          try {
            return new URL(url, 'https://www.facebook.com').searchParams.get(
              'fbid',
            )
          } catch {
            return null
          }
        })
        .filter((id: string | null) => id && !processedPhotoIds.has(id))

      const gatheredSettings = initialValues || {}
      let totalPhotos = 0
      for (const photoId of photoIds) {
        const waitForPhotoPage = promises.getUrlChangePromise()
        await clickOnPhotoById(photoId)
        await waitForPhotoPage

        // Wait for photo page to load (a little hacky)
        await promises.wait(400)

        const isUneditable = await checkForUneditableCoverPhoto()

        if (!isUneditable) {
          const value = await gatherEditPrivacyIconValue()

          if (value) {
            gatheredSettings[photoId] = value

            await updateStoredInitialValues(
              {
                [INITIAL_VALUE_KEY]: gatheredSettings,
              },
              FBRecommendationKey.FB_COVER_PHOTO_PRIVACY,
            )
            totalPhotos += 1
          }
        }

        processedPhotoIds.add(photoId)
        resumeData.set({ processedPhotoIds: [...processedPhotoIds] })

        await promises.wait(400)

        const waitForReturn = promises.getUrlChangePromise()
        window.history.back()
        await waitForReturn
      }

      if (totalPhotos === 0) {
        updateStoredInitialValues(
          { [INITIAL_VALUE_KEY]: null },
          FBRecommendationKey.FB_COVER_PHOTO_PRIVACY,
        )
      }

      return true
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_COVER_PHOTO_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    action: async (machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        updateStatisticsLastCount,
        initialValues: storedInitialValues,
        isDry,
      } = userContext

      if (storedInitialValues === null || storedInitialValues === undefined) {
        throw Error(
          'initialValues must be provided to update Cover Photo privacy',
        )
      }

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        INITIAL_VALUE_KEY,
        OLD_INITIAL_VALUE_KEY,
      )

      await waitForAlbumsToLoad({ timeoutMs: 5000 })

      // Check first (and exit early) if no cover photos
      if (!hasAlbum) {
        return undefined
      }

      goToAlbum('Cover photos')

      let coverPhotoIds = Object.keys(initialValues || {})

      const { resumeData } = additionalMachineData
      const processedPhotoIds = new Set(resumeData.data.processedPhotoIds || [])
      coverPhotoIds = coverPhotoIds.filter((id) => !processedPhotoIds.has(id))

      await scrollToBottom()

      for (const currentPhotoId of coverPhotoIds) {
        const waitForPhotoPage = promises.getUrlChangePromise()
        await clickOnPhotoById(currentPhotoId)
        await waitForPhotoPage

        // Wait for photo page to load (a little hacky)
        await promises.wait(400)

        const isUneditable = await checkForUneditableCoverPhoto()

        if (!isUneditable) {
          await setEditPrivacyIconValueAsStrictOrStricter(
            selectAudienceUtils.FRIENDS,
            selectAudienceUtils.ONLY_ME,
            isDry,
          )
        }

        processedPhotoIds.add(currentPhotoId)
        resumeData.set({ processedPhotoIds: [...processedPhotoIds] })

        await incrementTotalCount(FBRecommendationKey.FB_COVER_PHOTO_PRIVACY)
        await updateStatisticsLastCount(
          FBRecommendationKey.FB_COVER_PHOTO_PRIVACY,
          processedPhotoIds.size,
        )

        await promises.wait(400)

        const waitForReturn = promises.getUrlChangePromise()
        window.history.back()
        await waitForReturn
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTINGS_COVER_PHOTO_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    action: async (machineContext, userContext, additionalMachineData) => {
      const { initialValues: storedInitialValues, isDry } = userContext

      if (storedInitialValues === null || storedInitialValues === undefined) {
        throw Error(
          'initialValues must be provided to undo Cover Photo Privacy',
        )
      }

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        INITIAL_VALUE_KEY,
        OLD_INITIAL_VALUE_KEY,
      )

      await waitForAlbumsToLoad({ timeoutMs: 5000 })

      if (!hasAlbum('Cover photos')) {
        return undefined
      }

      goToAlbum('Cover photos')
      let coverPhotoIds = Object.keys(initialValues || {})

      const { resumeData } = additionalMachineData
      const processedPhotoIds = new Set(resumeData.data.processedPhotoIds || [])
      coverPhotoIds = coverPhotoIds.filter((id) => !processedPhotoIds.has(id))

      await scrollToBottom()

      for (const currentPhotoId of coverPhotoIds) {
        const waitForPhotoPage = promises.getUrlChangePromise()
        await clickOnPhotoById(currentPhotoId)
        await waitForPhotoPage

        // Wait for photo page to load (a little hacky)
        await promises.wait(400)

        const isUneditable = await checkForUneditableCoverPhoto()

        if (!isUneditable) {
          const initialSetting = initialValues[getPhotoIdFromCurrentUrl()]

          if (initialSetting) {
            await setEditPrivacyIconValueExact(initialSetting, isDry)
          }
        }

        processedPhotoIds.add(currentPhotoId)
        resumeData.set({ processedPhotoIds: [...processedPhotoIds] })

        await promises.wait(400)

        const waitForReturn = promises.getUrlChangePromise()
        window.history.back()
        await waitForReturn

        promises.wait(100)
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
